import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { DevicePortalChanges } from "@/components/devices/DevicePortalChanges";
import DeviceCreditHistory from "@/components/devices/DeviceCreditHistory";
import {
  Battery,
  Sun,
  SunMedium,
  Signal,
  ChevronDown,
  ChevronUp,
  ArchiveX,
  UserX,
  ExternalLink,
  Loader2,
  RefreshCw,
  Images,
  CheckCircle2,
  XCircle,
  Camera,
  HardDrive,
  Satellite,
  Activity,
  Bug,
  History,
  Coins,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DeviceDetails } from "./DeviceDetails";
import { useIsMobile } from "@/hooks/use-mobile";

export interface ProductionDevice {
  device_id: string;
  device_name: string | null;
  device_serial: string | null;
  device_firmware: string | null;
  device_last_updated: string | null;
  device_status?: string | null;
  imagekit: any;
  battery: any;
  cellular: any;
  camera_light_level: number | null;
  ambient_lux: number | null;
  device_description?: string | null;
  camera_image_quality?: string | null;
  images_allowed?: number | null;
  images_current?: number | null;
  group_name?: string | null;
  group_reference?: string | null;
  past_images?: any;
  camera_config?: any;
  enable_production_test?: boolean | null;
}

const getNetworkType = (rat: string | number | undefined) => {
  const map: Record<string, string> = {
    "1": "GSM (2G)",
    "2": "GPRS (2G)",
    "3": "EDGE (2.5G)",
    "4": "WCDMA (3G)",
    "5": "HSDPA (3.5G)",
    "6": "LTE (4G)",
    "7": "5G NR",
  };
  return map[String(rat)] || "Unknown";
};

const getRatLabel = (a: number | undefined) => {
  switch (a) {
    case 7: return "4G (LTE)";
    case 2: return "3G";
    case 0: return "2G";
    default: return "Unknown";
  }
};

const computeSignalScore = (n: {
  rsrp?: number; rsrq?: number; snr?: number; rssi?: number; csq?: number;
}): number | null => {
  if (!n) return null;
  let score = 0;
  let has = false;
  if (n.rsrp !== undefined) { score += Math.max(0, Math.min(60, ((n.rsrp + 120) / 40) * 60)); has = true; }
  if (n.rsrq !== undefined) { score += Math.max(0, Math.min(25, ((n.rsrq + 15) / 8) * 25)); has = true; }
  if (n.snr !== undefined) { score += Math.max(0, Math.min(15, (n.snr / 20) * 15)); has = true; }
  else if (n.rssi !== undefined) { score += Math.max(0, Math.min(40, ((n.rssi + 110) / 40) * 40)); has = true; }
  else if (n.csq !== undefined) { score += Math.max(0, Math.min(40, ((n.csq - 5) / 20) * 40)); has = true; }
  if (!has) return null;
  return Math.round(Math.max(0, Math.min(100, score)));
};

const getStatusDisplay = (
  statusRaw: string | null | undefined,
  lastUpdated: string | null | undefined
) => {
  const isDemo =
    typeof statusRaw === "string" && statusRaw.toLowerCase() === "demo";
  const lastDate = lastUpdated ? new Date(lastUpdated) : null;
  const isOffline =
    isDemo || !lastDate || isNaN(lastDate.getTime())
      ? false
      : (Date.now() - lastDate.getTime()) / 86400000 >= 2;

  if (isOffline && statusRaw !== "Hibernate") {
    return { label: "Offline", className: "bg-red-100 text-black", style: undefined as React.CSSProperties | undefined };
  }
  if (statusRaw === "RecordOnDemand" || statusRaw === "RetryOnDemand") {
    return { label: "On Demand", className: "bg-blue-100 text-black", style: undefined };
  }
  if (statusRaw === "Record") {
    return { label: "Active", className: "bg-green-100 text-black", style: undefined };
  }
  if (statusRaw === "Retry") {
    return { label: "Retry", className: "bg-green-100 text-black", style: undefined };
  }
  if (statusRaw === "Pause") {
    return { label: "Pause", className: "bg-orange-100 text-black", style: undefined };
  }
  if (statusRaw === "Hibernate") {
    return {
      label: "Hibernate",
      className: "text-white",
      style: { backgroundColor: "hsl(335, 41%, 14%)" } as React.CSSProperties,
    };
  }
  if (statusRaw === "Demo") {
    return { label: "Demo", className: "bg-yellow-100 text-black", style: undefined };
  }
  return { label: statusRaw || "Unknown", className: "bg-muted text-foreground", style: undefined };
};

const parseNetworkProbe = (raw: any): any | null => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const probe = parsed?.probe?.[0];
    return probe || null;
  } catch {
    return null;
  }
};

export const ProductionDeviceCard = ({
  device,
  onChanged,
  mode = "production",
}: {
  device: ProductionDevice;
  onChanged: () => void;
  mode?: "production" | "diagnosis";
}) => {
  const isDiagnosis = mode === "diagnosis";
  const [portalOpen, setPortalOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugEntries, setDebugEntries] = useState<
    Array<{ timestamp: string; message: string }>
  >([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewingDeviceId, setViewingDeviceId] = useState<string | null>(null);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [enableDebug, setEnableDebug] = useState(false);
  const [isUpdatingDebug, setIsUpdatingDebug] = useState(false);
  const [prodTestOpen, setProdTestOpen] = useState(false);
  const [prodTestLoading, setProdTestLoading] = useState(false);
  const [prodTestResult, setProdTestResult] = useState<any>(null);
  const [prodTestError, setProdTestError] = useState<string | null>(null);
  const [enableProductionTest, setEnableProductionTest] = useState(false);
  const [isUpdatingProdTest, setIsUpdatingProdTest] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const { logChange } = useDeviceChangeLog();

  const imagesAllowed =
    device.images_allowed != null ? Number(device.images_allowed) : null;
  const imagesUsed =
    device.images_current != null ? Number(device.images_current) : null;
  const imagesRemaining =
    imagesAllowed != null && imagesUsed != null
      ? Math.max(0, imagesAllowed - imagesUsed)
      : null;

  const status = getStatusDisplay(device.device_status, device.device_last_updated);

  const handleResetCounter = async () => {
    setIsResetting(true);
    try {
      const { error: dbError } = await supabase
        .from("device_details")
        .update({ images_current: 0 })
        .eq("device_id", device.device_id);
      if (dbError) throw dbError;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

      const params = new URLSearchParams();
      params.append("deviceID", device.device_id);
      params.append("field", "contract.imagesCurrent");
      params.append("value", "0");

      logChange(device.device_id, "contract.imagesCurrent", "0");

      const res = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      if (!res.ok) throw new Error("Failed to reset images counter");

      toast({ title: "Success", description: "Image counter reset to 0" });
      onChanged();
    } catch (e) {
      console.error("Error resetting image counter:", e);
      toast({
        title: "Error",
        description: "Failed to reset image counter",
        variant: "destructive",
      });
    } finally {
      setIsResetting(false);
      setResetOpen(false);
    }
  };

  useEffect(() => {
    const cc = device.camera_config;
    if (!cc) return;
    try {
      const parsed = typeof cc === "string" ? JSON.parse(cc) : cc;
      setEnableDebug(parsed?.enableDebug === 1 || parsed?.enableDebug === "1");
    } catch {
      /* ignore */
    }
  }, [device.camera_config]);

  useEffect(() => {
    if (typeof device.enable_production_test === "boolean") {
      setEnableProductionTest(device.enable_production_test);
      return;
    }
    const cc = device.camera_config;
    if (!cc) return;
    try {
      const parsed = typeof cc === "string" ? JSON.parse(cc) : cc;
      setEnableProductionTest(
        parsed?.enableProductionTest === 1 ||
          parsed?.enableProductionTest === "1" ||
          parsed?.enableProductionTest === true
      );
    } catch {
      /* ignore */
    }
  }, [device.enable_production_test, device.camera_config]);

  const handleDebugToggle = async (checked: boolean) => {
    setIsUpdatingDebug(true);
    setEnableDebug(checked);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

      const params = new URLSearchParams();
      params.append("deviceID", device.device_id);
      params.append("field", "cameraConfig.enableDebug");
      params.append("value", checked ? "1" : "0");

      logChange(device.device_id, "cameraConfig.enableDebug", checked ? "1" : "0");

      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      if (!response.ok) throw new Error("Failed to update debug setting");

      const { data, error } = await supabase
        .from("device_details")
        .select("camera_config")
        .eq("device_id", device.device_id)
        .single();
      if (error) throw error;
      if (data?.camera_config) {
        const cc = typeof data.camera_config === "string"
          ? JSON.parse(data.camera_config)
          : data.camera_config;
        setEnableDebug(cc?.enableDebug === 1 || cc?.enableDebug === "1");
      }
      toast({ title: "Success", description: "Debug setting updated" });
    } catch (e) {
      console.error(e);
      setEnableDebug(!checked);
      toast({
        title: "Error",
        description: "Failed to update debug setting",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingDebug(false);
    }
  };

  const handleProductionTestToggle = async (checked: boolean) => {
    setIsUpdatingProdTest(true);
    setEnableProductionTest(checked);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No session found");

      const params = new URLSearchParams();
      params.append("deviceID", device.device_id);
      params.append("field", "cameraConfig.enableProductionTest");
      params.append("value", checked ? "1" : "0");

      logChange(
        device.device_id,
        "cameraConfig.enableProductionTest",
        checked ? "1" : "0"
      );

      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      if (!response.ok) throw new Error("Failed to update production test setting");

      // Persist locally so the toggle survives page reload before BuildShip echoes back
      await supabase
        .from("device_details")
        .update({ enable_production_test: checked })
        .eq("device_id", device.device_id);

      const { data, error } = await supabase
        .from("device_details")
        .select("camera_config, enable_production_test")
        .eq("device_id", device.device_id)
        .single();
      if (error) throw error;
      if (typeof data?.enable_production_test === "boolean") {
        setEnableProductionTest(data.enable_production_test);
      }
      toast({ title: "Success", description: "Production test setting updated" });
    } catch (e) {
      console.error(e);
      setEnableProductionTest(!checked);
      toast({
        title: "Error",
        description: "Failed to update production test setting",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingProdTest(false);
    }
  };

  const fetchProductionTest = async () => {
    setProdTestLoading(true);
    setProdTestError(null);
    try {
      const { data: tokenResponse, error: tokenError } =
        await supabase.functions.invoke("get-buildship-token");
      if (tokenError || !tokenResponse?.token)
        throw new Error("Failed to get BuildShip token");

      const res = await fetch("https://api.snapshot.systems/device-diagnostics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenResponse.token}`,
        },
        body: JSON.stringify({ deviceID: device.device_id }),
      });
      if (!res.ok) throw new Error(`Diagnostics failed: ${res.status}`);
      const json = await res.json();
      setProdTestResult(json);
    } catch (e) {
      console.error(e);
      setProdTestError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setProdTestLoading(false);
    }
  };

  const toggleProdTest = () => {
    const next = !prodTestOpen;
    setProdTestOpen(next);
    if (next && !prodTestResult && !prodTestLoading) fetchProductionTest();
  };

  const battery = (device.battery as any) || {};
  const cellular = (device.cellular as any) || {};
  const imagekit = (device.imagekit as any) || {};

  const thumbnail = imagekit?.thumbnailUrl || imagekit?.url;

  const recentImages: Array<{ url: string; thumb: string }> = (() => {
    const raw = device.past_images;
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr
      .map((item: any) => {
        if (!item) return null;
        if (typeof item === "string") return { url: item, thumb: item };
        const url = item.url || item.imageUrl || item.thumbnailUrl;
        const thumb = item.thumbnailUrl || item.url || item.imageUrl;
        if (!url) return null;
        return { url, thumb };
      })
      .filter(Boolean) as Array<{ url: string; thumb: string }>;
  })();

  const handleArchive = async () => {
    setBusy(true);
    try {
      const res = await fetch("https://i56o7w.buildship.run/device-archive", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ deviceID: device.device_id }),
      });
      if (!res.ok) throw new Error("Failed to archive device");

      try {
        const { data: existing } = await supabase.storage
          .from("device-test-images")
          .list(device.device_id);
        if (existing && existing.length > 0) {
          await supabase.storage
            .from("device-test-images")
            .remove(existing.map((f) => `${device.device_id}/${f.name}`));
        }
        await supabase
          .from("device_details")
          .update({ test_image_url: null })
          .eq("device_id", device.device_id);
      } catch (e) {
        console.error("Test image cleanup failed:", e);
      }

      toast({ title: "Archived", description: "Device images archived." });
      onChanged();
    } catch (e) {
      console.error(e);
      toast({
        title: "Error",
        description: "Failed to archive device",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setArchiveOpen(false);
    }
  };

  const handleRemoveAssignments = async () => {
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;

      const { error: aErr } = await supabase
        .from("device_assignments")
        .delete()
        .eq("device_id", device.device_id);
      if (aErr) throw aErr;

      const { error: pErr } = await supabase
        .from("device_credit_packages")
        .delete()
        .eq("device_id", device.device_id);
      if (pErr) throw pErr;

      const { error: cErr } = await supabase
        .from("device_assignment_credits")
        .delete()
        .eq("device_id", device.device_id);
      if (cErr) throw cErr;

      const { data: tokenResponse, error: tokenError } =
        await supabase.functions.invoke("get-buildship-token");
      if (tokenError || !tokenResponse?.token)
        throw new Error("Failed to get BuildShip token");

      const buildshipResponse = await fetch(
        "https://api.snapshot.systems/device-unassign",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.token}`,
          },
          body: JSON.stringify({ deviceID: device.device_id }),
        }
      );
      if (!buildshipResponse.ok) {
        throw new Error(
          `device-unassign failed: ${buildshipResponse.status}`
        );
      }

      await supabase
        .from("device_details")
        .update({
          group_id: null,
          group_name: null,
          group_reference: null,
          organization_id: null,
        })
        .eq("device_id", device.device_id);

      await supabase.from("device_assignment_audit").insert({
        device_id: device.device_id,
        user_id: currentUserId,
        action: "assignments_removed",
        assignment_type: "system",
        metadata: {
          removed_at: new Date().toISOString(),
          removed_by: currentUserId,
          source: "production_page",
        },
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["production-devices"] }),
        queryClient.invalidateQueries({ queryKey: ["device-details"] }),
      ]);

      toast({
        title: "Assignments Removed",
        description: `${device.device_name || device.device_id} unassigned.`,
      });
      onChanged();
    } catch (e) {
      console.error(e);
      toast({
        title: "Failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setRemoveOpen(false);
    }
  };

  const fetchDebug = async () => {
    setDebugLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch("https://i56o7w.buildship.run/deviceDebug", {
        method: "POST",
        headers,
        body: JSON.stringify({ deviceID: device.device_id }),
      });
      const text = await res.text();
      try {
        const json = JSON.parse(text);
        if (json.data && typeof json.data === "object") {
          const entries = Object.entries(json.data)
            .map(([timestamp, message]) => ({
              timestamp,
              message: String(message),
            }))
            .sort(
              (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
            );
          setDebugEntries(entries);
        } else {
          setDebugEntries([]);
        }
      } catch {
        setDebugEntries([]);
      }
    } catch (e) {
      console.error(e);
      toast({
        title: "Debug fetch failed",
        description: "Could not load debug data",
        variant: "destructive",
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const toggleDebug = () => {
    const next = !debugOpen;
    setDebugOpen(next);
    if (next && debugEntries.length === 0) fetchDebug();
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col lg:flex-row lg:items-start">
        {/* Image */}
        <div className="lg:w-64 w-full bg-muted shrink-0">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt={device.device_name || device.device_id}
              className="w-full h-48 object-cover"
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center text-xs text-muted-foreground">
              No image
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0 p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h3 className="text-lg font-semibold truncate">
                  {device.device_name || "Unnamed device"}
                </h3>
                <span
                  className={`text-xs font-semibold rounded px-2 py-1 whitespace-nowrap ${status.className}`}
                  style={status.style}
                >
                  {status.label}
                </span>
              </div>
              <div className="text-xs text-muted-foreground space-x-2">
                <span>ID: {device.device_id}</span>
                {device.device_serial && (
                  <span>· Serial: {device.device_serial}</span>
                )}
                {device.device_firmware && (
                  <span>· FW: {device.device_firmware}</span>
                )}
              </div>
              {device.device_last_updated && (
                <div className="text-xs text-muted-foreground mt-1">
                  Last updated:{" "}
                  {new Date(device.device_last_updated).toLocaleString()}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap ml-auto justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setViewingDeviceId(device.device_id)}
              >
                <ExternalLink className="h-4 w-4" /> View Full Details
              </Button>
              {!isDiagnosis && (<>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setArchiveOpen(true)}
                disabled={busy}
              >
                <ArchiveX className="h-4 w-4" /> Archive Images
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200"
                onClick={() => setResetOpen(true)}
                disabled={isResetting}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isResetting ? "animate-spin" : ""}`}
                />{" "}
                 Reset Image Counter
              </Button>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => setRemoveOpen(true)}
                disabled={busy}
              >
                <CheckCircle2 className="h-4 w-4" /> Pass Inspection
              </Button>
              </>)}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
            <Stat
              icon={<Battery className="h-4 w-4 text-blue-600" />}
              label="Battery"
              value={
                battery?.batteryLevel != null ? `${battery.batteryLevel}%` : "—"
              }
            />
            <Stat
              icon={<Sun className="h-4 w-4 text-amber-600" />}
              label="Solar V"
              value={
                battery?.chargeVin != null ? `${battery.chargeVin}V` : "—"
              }
            />
            <Stat
              icon={<SunMedium className="h-4 w-4 text-yellow-600" />}
              label="Light"
              value={
                device.ambient_lux != null
                  ? `${device.ambient_lux} lx`
                  : "—"
              }
            />
            <Stat
              icon={<Signal className="h-4 w-4 text-violet-600" />}
              label="Network"
              value={(() => {
                const p = parseNetworkProbe(cellular?.networkProbe);
                if (!p) return "—";
                const rat = getRatLabel(p.a);
                return p.n ? `${rat} · ${p.n}` : rat;
              })()}
            />
            <Stat
              icon={<Signal className="h-4 w-4 text-violet-600" />}
              label="Signal"
              value={(() => {
                const p = parseNetworkProbe(cellular?.networkProbe);
                const score = p ? computeSignalScore(p) : null;
                return score != null ? `${score}%` : "—";
              })()}
            />
            <Stat
              icon={<Images className="h-4 w-4 text-teal-600" />}
              label="Images Left"
              value={
                imagesRemaining != null ? imagesRemaining.toLocaleString() : "—"
              }
              sub={
                imagesAllowed != null || imagesUsed != null
                  ? `${imagesUsed != null ? imagesUsed.toLocaleString() : "—"} of ${
                      imagesAllowed != null ? imagesAllowed.toLocaleString() : "—"
                    } used`
                  : undefined
              }
            />
          </div>

          {/* Debug dropdown */}
          <div className="border-t pt-2 min-w-0">
            {/* Recent Images */}
            <div className="pb-2 mb-2 border-b min-w-0">
              <button
                type="button"
                onClick={() => setImagesOpen((v) => !v)}
                className="flex items-center gap-2 text-sm font-medium hover:underline"
              >
                {imagesOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                <Images className="h-4 w-4" />
                Recent Images ({recentImages.length})
              </button>
              {imagesOpen && (
                <div className="mt-2 max-w-full max-h-80 overflow-y-auto rounded border bg-muted/30 p-2">
                  {recentImages.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      No recent images available.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {recentImages.map((img, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(img.url)}
                          className="block aspect-square overflow-hidden rounded border bg-background hover:opacity-80"
                        >
                          <img
                            src={img.thumb}
                            alt={`Recent ${i + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <>
            {/* Production Test */}
            <div className="pb-2 mb-2 border-b min-w-0">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleProdTest}
                  className="flex items-center gap-2 text-sm font-medium hover:underline"
                >
                  {prodTestOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <Activity className="h-4 w-4" />
                  {isDiagnosis ? "Diagnostic" : "Production Test"}
                  {prodTestLoading && (
                    <Loader2 className="h-3 w-3 animate-spin ml-1" />
                  )}
                </button>
                {!isDiagnosis && (
                <div className="flex items-center gap-2 ml-2">
                  <Switch
                    id={`enable-prod-test-${device.device_id}`}
                    checked={enableProductionTest}
                    onCheckedChange={handleProductionTestToggle}
                    disabled={isUpdatingProdTest}
                  />
                  <Label
                    htmlFor={`enable-prod-test-${device.device_id}`}
                    className="text-xs cursor-pointer"
                  >
                    Enable Production Test
                  </Label>
                  {isUpdatingProdTest && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>
                )}
                {prodTestOpen && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={fetchProductionTest}
                    disabled={prodTestLoading}
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${prodTestLoading ? "animate-spin" : ""}`}
                    />
                    <span className="ml-1 text-xs">Refresh</span>
                  </Button>
                )}
              </div>
              {prodTestOpen && (
                <div className="mt-2">
                  <ProductionTestResults
                    loading={prodTestLoading}
                    error={prodTestError}
                    result={prodTestResult}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleDebug}
                className="flex items-center gap-2 text-sm font-medium hover:underline"
              >
                {debugOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                <Bug className="h-4 w-4" />
                Debug Data
                {debugLoading && (
                  <Loader2 className="h-3 w-3 animate-spin ml-1" />
                )}
              </button>
              {!isDiagnosis && (
              <div className="flex items-center gap-2 ml-2">
                <Switch
                  id={`enable-debug-${device.device_id}`}
                  checked={enableDebug}
                  onCheckedChange={handleDebugToggle}
                  disabled={isUpdatingDebug}
                />
                <Label
                  htmlFor={`enable-debug-${device.device_id}`}
                  className="text-xs cursor-pointer"
                >
                  Enable Debug
                </Label>
                {isUpdatingDebug && (
                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                )}
              </div>
              )}
              {debugOpen && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2"
                  onClick={fetchDebug}
                  disabled={debugLoading}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${debugLoading ? "animate-spin" : ""}`}
                  />
                  <span className="ml-1 text-xs">Refresh</span>
                </Button>
              )}
            </div>
            {debugOpen && (
              <div className="mt-2 max-h-64 max-w-full overflow-auto rounded border bg-muted/30 p-2 text-xs font-mono text-left">
                {debugLoading && debugEntries.length === 0 ? (
                  <div className="text-muted-foreground">Loading…</div>
                ) : debugEntries.length === 0 ? (
                  <div className="text-muted-foreground">
                    No debug data available.
                  </div>
                ) : (
                  debugEntries.map((e, i) => (
                    <div
                      key={i}
                      className="py-0.5 break-all whitespace-pre-wrap"
                    >
                      <span className="text-muted-foreground">
                        {e.timestamp}
                      </span>{" "}
                      — {e.message}
                    </div>
                  ))
                )}
              </div>
            )}
            </>
            {isDiagnosis && (
              <>
                <div className="pt-2 mt-2 border-t min-w-0">
                  <button
                    type="button"
                    onClick={() => setPortalOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-medium hover:underline"
                  >
                    {portalOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <History className="h-4 w-4" />
                    Portal Changes
                  </button>
                  {portalOpen && (
                    <div className="mt-2">
                      <DevicePortalChanges deviceId={device.device_id} />
                    </div>
                  )}
                </div>
                <div className="pt-2 mt-2 border-t min-w-0">
                  <button
                    type="button"
                    onClick={() => setCreditOpen((v) => !v)}
                    className="flex items-center gap-2 text-sm font-medium hover:underline"
                  >
                    {creditOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    <Coins className="h-4 w-4" />
                    Credit History
                  </button>
                  {creditOpen && (
                    <div className="mt-2">
                      <DeviceCreditHistory deviceId={device.device_id} autoLoad />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive device images?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive all images for {device.device_name || device.device_id} and clear the AI test image.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={busy}>
              {busy ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pass inspection and release device?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks {device.device_name || device.device_id} as having passed production inspection. It will be removed from production and become available to assign to another customer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveAssignments} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white">
              {busy ? "Releasing…" : "Pass Inspection"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Image Counter?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the image counter back to zero, but will not delete any images.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetCounter}
              disabled={isResetting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isResetting ? "Resetting…" : "Reset Counter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Full Device Details Dialog/Sheet */}
      {isMobile ? (
        <Sheet open={!!viewingDeviceId} onOpenChange={(open) => !open && setViewingDeviceId(null)}>
          <SheetContent side="right" className="p-3 w-full sm:max-w-[85vw] overflow-hidden">
            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
              {viewingDeviceId && (
                <DeviceDetails deviceId={viewingDeviceId} />
              )}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={!!viewingDeviceId} onOpenChange={(open) => !open && setViewingDeviceId(null)}>
          <DialogContent className="w-[90vw] h-[80vh] max-w-[80vw] max-h-[80vh] p-0 overflow-hidden">
            <ScrollArea className="h-full w-full max-w-full overflow-x-hidden">
              {viewingDeviceId && (
                <DeviceDetails deviceId={viewingDeviceId} />
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogHeader>
            <DialogTitle className="sr-only">Image preview</DialogTitle>
          </DialogHeader>
          {lightbox && (
            <img
              src={lightbox}
              alt="Preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Stat = ({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) => (
  <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5">
    {icon}
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">
        {label}
      </div>
      <div className="text-sm font-medium truncate">{value}</div>
      {sub && (
        <div className="text-[10px] text-muted-foreground truncate">{sub}</div>
      )}
    </div>
  </div>
);

const PassBadge = ({ pass }: { pass: boolean }) =>
  pass ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 border border-emerald-200">
      <CheckCircle2 className="h-3 w-3" /> Pass
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 border border-red-200">
      <XCircle className="h-3 w-3" /> Fail
    </span>
  );

const DiagTile = ({
  icon,
  title,
  pass,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  pass: boolean;
  children?: React.ReactNode;
}) => (
  <div
    className={`rounded-md border p-2 ${pass ? "bg-emerald-50/30 border-emerald-200" : "bg-red-50/30 border-red-200"}`}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-xs font-medium">
        {icon}
        {title}
      </div>
      <PassBadge pass={pass} />
    </div>
    {children && (
      <div className="mt-1 text-[11px] text-muted-foreground space-y-0.5">
        {children}
      </div>
    )}
  </div>
);

const Bar = ({ value, max = 100 }: { value: number; max?: number }) => {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
      <div
        className="h-full bg-emerald-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const ProductionTestResults = ({
  loading,
  error,
  result,
}: {
  loading: boolean;
  error: string | null;
  result: any;
}) => {
  if (loading && !result) {
    return (
      <div className="rounded border bg-muted/30 p-3 text-xs text-muted-foreground flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running diagnostics…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
        {error}. Click Refresh to retry.
      </div>
    );
  }
  if (!result) {
    return (
      <div className="rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
        Run the test to see results.
      </div>
    );
  }

  const data = result?.data ?? {};
  const allPassed = !!data.allPassed;
  const timestamp = result?.id ? new Date(result.id) : null;
  const tsLabel =
    timestamp && !isNaN(timestamp.getTime()) ? timestamp.toLocaleString() : null;

  const sd = data.sd ?? {};
  const c1 = data.camera1 ?? {};
  const c2 = data.camera2 ?? {};
  const gnss = data.gnss ?? {};
  const modem = data.modem ?? {};
  const ambient = data.ambient ?? {};
  const batt = data.battery ?? {};

  return (
    <div className="space-y-2">
      <div
        className={`rounded-md border px-3 py-2 flex items-center justify-between ${
          allPassed
            ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : "bg-red-50 border-red-200 text-red-800"
        }`}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          {allPassed ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          {allPassed
            ? "All systems passed"
            : data.failureSummary || "Diagnostics failed"}
        </div>
        {tsLabel && (
          <div className="text-[11px] opacity-80">{tsLabel}</div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        <DiagTile
          icon={<HardDrive className="h-3.5 w-3.5" />}
          title="SD Card"
          pass={!!sd.pass}
        />
        <DiagTile
          icon={<Camera className="h-3.5 w-3.5" />}
          title="Camera 1"
          pass={!!c1.pass}
        />
        <DiagTile
          icon={<Camera className="h-3.5 w-3.5" />}
          title="Camera 2"
          pass={!!c2.pass}
        />
        <DiagTile
          icon={<Satellite className="h-3.5 w-3.5" />}
          title="GNSS"
          pass={!!gnss.pass}
        />
        <DiagTile
          icon={<Signal className="h-3.5 w-3.5" />}
          title="Modem"
          pass={!!modem.pass}
        >
          <div>Network: {getNetworkType(modem.rat)}</div>
          {modem.strength != null && (
            <>
              <div className="flex items-center justify-between">
                <span>Signal</span>
                <span>{modem.strength}%</span>
              </div>
              <Bar value={Number(modem.strength) || 0} />
            </>
          )}
          {modem.quality != null && <div>Quality: {modem.quality}</div>}
          <div className="flex items-center gap-1">
            <span>Signal check:</span>
            <PassBadge pass={!!modem.signalPass} />
          </div>
        </DiagTile>
        <DiagTile
          icon={<Sun className="h-3.5 w-3.5" />}
          title="Ambient"
          pass={!!ambient.pass}
        >
          {ambient.lux != null && <div>{ambient.lux} lx</div>}
        </DiagTile>
        <DiagTile
          icon={<Battery className="h-3.5 w-3.5" />}
          title="Battery"
          pass={!!batt.voltagePass && !!batt.socPass}
        >
          {batt.soc != null && (
            <>
              <div className="flex items-center justify-between">
                <span>SoC</span>
                <span>{batt.soc}%</span>
              </div>
              <Bar value={Number(batt.soc) || 0} />
            </>
          )}
          {batt.voltage != null && <div>Voltage: {batt.voltage} V</div>}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              V <PassBadge pass={!!batt.voltagePass} />
            </span>
            <span className="inline-flex items-center gap-1">
              SoC <PassBadge pass={!!batt.socPass} />
            </span>
          </div>
        </DiagTile>
      </div>
    </div>
  );
};